import LayoutSwitch from "common/Switch/LayoutSwitch";
import FilterOutlined from "@ant-design/icons/FilterOutlined";
import MenuOutlined from "@ant-design/icons/MenuOutlined";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import TableOutlined from "@ant-design/icons/TableOutlined";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import FiltersChips from "common/Filters/FiltersChips/FiltersChips";
import { backtestingFilterTypeToTitle } from "@shared/features/bots/constants";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import Button from "common/Buttons/Button";
import FiltersCounter from "common/Filters/common/FiltersCounter";
import styled, { useTheme } from "styled-components";
import { LayoutGrid, List, Plus } from "lucide-react";
import {
  NEW_LOOK_ICON_STROKE,
  NEW_LOOK_PAGE_GUTTER_VAR,
} from "common/NewLook/layout";
import useMedia from "utils/hooks/useMedia";
import { SINGLE_FILTERS_ROW_BREAKPOINT } from "@shared/features/filters/constants";
import { useDispatch, useSelector } from "react-redux";
import StickyHeader from "common/Headers/StickyHeader";
import { getCreateBotRoute } from "utils/routes";
import {
  BacktestingSort,
  BotType,
  LanguageNameSpaces,
  QueryBotType,
  SortingOrder,
} from "@shared/types";
import { useHistory, useRouteMatch } from "react-router-dom";
import SortSelect from "common/Select/SortSelect";
import { BASE_BOTS_PATH } from "common/constants";
import {
  dcaBotBacktestingFilters,
  signalsBotBacktestingFilters,
} from "@shared/slices/filters.slice";
import { QUERY_BOT_TYPE_TO_BOT_TYPE } from "@shared/common/constants";
import { useBottomNavigator } from "utils/hooks/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import PageIntroActions from "features/bots/NewLook/PageIntroActions";
import NewLookFiltersButton from "features/bots/NewLook/NewLookFiltersButton";
import { NewLookSegmented } from "features/bots/NewLook/NewLookSegmented";

interface BacktestingWrapperProps {
  /** The search field (owned by the page, which places it in new look). */
  SearchInput: React.JSX.Element;
  searchInputValue?: string;
  isCardsView: boolean;
  setIsCardsView: (isCardsView: boolean) => void;
  setSearchValue: (searchValue: string) => void;
  useFiltersChips?: boolean;
  padding?: number;
  sortBy?: BacktestingSort;
  setSortBy: (sortBy: BacktestingSort) => void;
  sortDirection: SortingOrder;
  setSortDirection: (sortDirection: SortingOrder) => void;
}

const BacktestingWrapper = ({
  SearchInput,
  searchInputValue,
  isCardsView,
  setIsCardsView,
  setSearchValue,
  useFiltersChips,
  padding,
  sortBy,
  setSortBy,
  sortDirection,
  setSortDirection,
}: BacktestingWrapperProps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t: tBots, i18n } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { useMobileSearch, search: mobileSearch } =
    useContext(PageWrapperContext);
  const isSingleFiltersChipsRow = useMedia(
    [`(max-width: ${SINGLE_FILTERS_ROW_BREAKPOINT})`],
    [true],
    false
  );

  const match = useRouteMatch(BASE_BOTS_PATH);
  const { botType: queryBotType } = (match?.params || {}) as {
    botType?: QueryBotType;
  };
  const botType =
    (queryBotType ? QUERY_BOT_TYPE_TO_BOT_TYPE[queryBotType] : undefined) ??
    BotType.SIGNAL;
  const filters =
    botType === BotType.SIGNAL
      ? signalsBotBacktestingFilters
      : dcaBotBacktestingFilters;

  const [showFilters, setShowFilters] = useState(false);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isNewLook } = useTheme();
  const isNewLookPage = isNewLook && !isBottomNavigator;

  const searchValue = useMobileSearch
    ? mobileSearch.value
    : (searchInputValue as string);

  const numActiveFilters = useSelector(
    filters.selectors.selectNumActiveFilters
  );

  useEffect(() => {
    setSearchValue(searchValue || "");
  }, [searchValue]);

  const sortSelect = useMemo(
    () => (
      <SortSelect
        setSortBy={setSortBy}
        sortBy={sortBy}
        sortDirection={sortDirection}
        setSortDirection={setSortDirection}
        sortValues={SORT_VALUES}
        rounded={true}
        isSingleFiltersChipsRow={isSingleFiltersChipsRow}
        placeholder={tBots("backtesting.sort.default")}
        labelMapper={(value) => tBots(value)}
      />
    ),
    [sortBy, sortDirection, isSingleFiltersChipsRow, i18n.language]
  );

  const layoutSwitch = useMemo(
    () => (
      <LayoutSwitch
        layoutIcons={[<MenuOutlined />, <TableOutlined />]}
        selectedLayoutIndex={isCardsView ? 1 : 0}
        onClick={() => setIsCardsView(!isCardsView)}
      />
    ),
    [isCardsView]
  );

  const chipsExtraComponent = useMemo(() => {
    return (
      <>
        {sortSelect}
        {!isBottomNavigator && layoutSwitch}
      </>
    );
  }, [sortSelect, isBottomNavigator, layoutSwitch]);

  const getExpandableChipsContent = () => {
    return (
      <ExpandCollapse expanded={showFilters || isSingleFiltersChipsRow}>
        <div
          style={{
            paddingBottom: isBottomNavigator ? 0 : padding,
          }}
        >
          <FiltersChips
            filtersActions={filters.actions}
            filtersSelectors={filters.selectors}
            filterTypeToTitle={backtestingFilterTypeToTitle}
            extraComponent={chipsExtraComponent}
          />
        </div>
      </ExpandCollapse>
    );
  };

  const actionsRow = (
    <ActionsWrapper style={{ paddingBottom: padding }}>
      {sortSelect}
      {SearchInput}
      {layoutSwitch}
      {useFiltersChips && (
        <FiltersButtonWrapper>
          <Button onClick={() => setShowFilters(!showFilters)}>
            <FilterOutlined />
            {tBots("labels.filters")}
          </Button>
          <FiltersCounter numActiveFilters={numActiveFilters} />
        </FiltersButtonWrapper>
      )}
      <Button
        type="primary"
        onClick={() => {
          if (botType) {
            history.push(getCreateBotRoute(botType));
          }
        }}
      >
        <PlusOutlined /> {tBots("buttons.create")}
      </Button>
    </ActionsWrapper>
  );

  if (isNewLookPage) {
    // New look (desktop): the page actions in the page toolbar (filters,
    // sorting, the list/cards switch and create, like the trades page); the
    // search sits in the list's own header. The filter chips open under the
    // toolbar.
    const onCreateClick = () => {
      if (botType) {
        history.push(getCreateBotRoute(botType));
      }
    };
    return (
      <NewLookHeader>
        <PageIntroActions
          actions={
            <>
              {useFiltersChips && !isSingleFiltersChipsRow && (
                <NewLookFiltersButton
                  open={showFilters}
                  numActiveFilters={numActiveFilters}
                  onClick={() => setShowFilters(!showFilters)}
                  onReset={() => dispatch(filters.actions.selectAll(undefined))}
                />
              )}
              {sortSelect}
              <NewLookSegmented<string>
                value={isCardsView ? "cards" : "list"}
                onChange={(value) => setIsCardsView(value === "cards")}
                options={[
                  {
                    value: "list",
                    title: tBots("labels.table_view"),
                    icon: (
                      <SegmentIcon>
                        <List size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
                      </SegmentIcon>
                    ),
                  },
                  {
                    value: "cards",
                    title: tBots("labels.cards_view"),
                    icon: (
                      <SegmentIcon>
                        <LayoutGrid
                          size={16}
                          strokeWidth={NEW_LOOK_ICON_STROKE}
                        />
                      </SegmentIcon>
                    ),
                  },
                ]}
              />
              <Button type="primary" onClick={onCreateClick}>
                <Plus size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
                {tBots("buttons.create")}
              </Button>
            </>
          }
          deps={[
            showFilters,
            numActiveFilters,
            useFiltersChips,
            isSingleFiltersChipsRow,
            botType,
            sortSelect,
            isCardsView,
            i18n.language,
          ]}
        />
        {useFiltersChips && (
          <ExpandCollapse expanded={showFilters || isSingleFiltersChipsRow}>
            <div style={{ paddingBottom: 16 }}>
              <FiltersChips
                filtersActions={filters.actions}
                filtersSelectors={filters.selectors}
                filterTypeToTitle={backtestingFilterTypeToTitle}
              />
            </div>
          </ExpandCollapse>
        )}
      </NewLookHeader>
    );
  }

  if (useFiltersChips) {
    const headerContent = (
      <>
        {!isSingleFiltersChipsRow && actionsRow}
        {useFiltersChips && getExpandableChipsContent()}
      </>
    );
    if (isBottomNavigator) {
      return headerContent;
    }
    return (
      <StickyHeader
        style={{
          padding: isSingleFiltersChipsRow
            ? `${padding}px 0 0`
            : `${padding}px ${padding}px 0`,
        }}
      >
        {headerContent}
      </StickyHeader>
    );
  }

  return (
    <div style={{ padding: `${padding}px ${padding}px 0` }}>{actionsRow}</div>
  );
};

export default BacktestingWrapper;

const SORT_VALUES = Object.values(BacktestingSort);

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  column-gap: 8px;
`;
const FiltersButtonWrapper = styled.div`
  position: relative;
`;
const NewLookHeader = styled.div`
  padding: 16px ${NEW_LOOK_PAGE_GUTTER_VAR} 0;
`;
const SegmentIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
`;
